import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import toast, { Toaster } from 'react-hot-toast';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import EmailDetail from './pages/EmailDetail';
import ApprovedQueue from './pages/ApprovedQueue';
import Settings from './pages/Settings';
import EDeskLayout from './components/EDeskLayout';
import EDeskMailboxTable from './components/EDeskMailboxTable';
import { reviewAPI } from './services/api';
import './index.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

function MailboxView() {
  const [activeTab, setActiveTab] = useState('Pre-sales');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPlatform, setSelectedPlatform] = useState('all');
  const navigate = useNavigate();

  // Attach Server-Sent Events (SSE) Live Feed listener
  React.useEffect(() => {
    const eventSource = new EventSource(`${API_BASE_URL}/emails/stream`);

    eventSource.addEventListener('new_email', (event: any) => {
      try {
        const data = JSON.parse(event.data);
        toast.success(`📬 Real-Time Email Arrived: ${data.subject || 'New Message'}`, { duration: 4000 });
        queryClient.invalidateQueries({ queryKey: ['reviewQueue'] });
        queryClient.invalidateQueries({ queryKey: ['reviewStats'] });
      } catch (e) {
        console.error('Error parsing SSE payload:', e);
      }
    });

    return () => {
      eventSource.close();
    };
  }, []);

  // Fetch live emails from review queue API with 3-second real-time auto-polling
  const { data: emailsData, isError } = useQuery({
    queryKey: ['reviewQueue', activeTab, searchQuery, selectedPlatform],
    queryFn: async () => {
      let status: string | undefined = undefined;
      let category: string | undefined = undefined;

      if (activeTab === 'Pending Review' || activeTab === 'Pre-sales' || activeTab === 'New') status = 'pending_review';
      if (activeTab === 'Approved' || activeTab === 'Approved & Ready' || activeTab === 'My tickets' || activeTab === 'To do') status = 'approved';
      if (activeTab === 'Rejected' || activeTab === 'Mentioned') status = 'rejected';
      if (activeTab === 'Sent' || activeTab === 'Sent Responses' || activeTab === 'Unassigned' || activeTab === 'Waiting') status = 'sent';

      // Category tab mappings
      if (activeTab === 'Customer Inquiries') category = 'customer_question';
      if (activeTab === 'Shipping & Delivery') category = 'shipping_question';
      if (activeTab === 'Damaged / Defective') category = 'damaged_defective';
      if (activeTab === 'Returns & Refunds') category = 'return_policy';
      if (activeTab === 'Order Cancellations') category = 'order_cancellation';
      if (activeTab === 'Product Questions') category = 'product_question';

      const res = await reviewAPI.getReviewQueue({
        limit: 100,
        response_status: status,
        category: category,
        platform: selectedPlatform !== 'all' ? selectedPlatform : undefined,
      });
      return res.data;
    },
    refetchInterval: 2000, // 2-second real-time polling
  });

  const rawEmails = emailsData?.data ?? (Array.isArray(emailsData) ? emailsData : null);
  const emails = isError ? null : rawEmails;

  return (
    <EDeskLayout
      activeTab={activeTab}
      onTabChange={(tab) => setActiveTab(tab)}
      searchQuery={searchQuery}
      onSearchChange={(query) => setSearchQuery(query)}
      selectedPlatform={selectedPlatform}
      onPlatformChange={(platform: string) => setSelectedPlatform(platform)}
      totalCount={Array.isArray(emails) ? emails.length : 4}
    >
      <EDeskMailboxTable
        emails={emails}
        activeTab={activeTab}
        onSelectTicket={(ticket) => navigate(`/review/${ticket.id}`)}
      />
    </EDeskLayout>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Router>
          <Toaster position="top-right" />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route
              path="/*"
              element={
                <ProtectedRoute>
                  <Routes>
                    <Route path="/" element={<MailboxView />} />
                    <Route path="/emails" element={<MailboxView />} />
                    <Route path="/review" element={<MailboxView />} />
                    <Route path="/review/:id" element={<EDeskLayout><EmailDetail /></EDeskLayout>} />
                    <Route path="/approved" element={<EDeskLayout><ApprovedQueue /></EDeskLayout>} />
                    <Route path="/dashboard" element={<EDeskLayout><Dashboard /></EDeskLayout>} />
                    <Route path="/settings" element={<EDeskLayout><Settings /></EDeskLayout>} />
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </ProtectedRoute>
              }
            />
          </Routes>
        </Router>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
